import type { Household, HouseholdRole, InstructionStep, Recipe, User } from '../types';
import { canAccessGuidedClientsAdmin, isGuidedClientHousehold } from './guidedClientHousehold';

/** Household managers can share a temporary recipe. Editors and viewers cannot. */
const MANAGER_ROLES: readonly HouseholdRole[] = ['owner', 'admin'];

export type RecipeShareActor = Pick<User, 'uid' | 'role' | 'userType'>;

export function userManagesHousehold(user: RecipeShareActor, household: Household): boolean {
    if (user.role === 'superuser') return true;
    if (isGuidedClientHousehold(household) && canAccessGuidedClientsAdmin(user)) return true;
    if (household.ownerId === user.uid) return true;
    const role = household.memberRoles?.[user.uid] ?? 'viewer';
    return MANAGER_ROLES.includes(role);
}

export function managedHouseholdIds(user: RecipeShareActor, households: readonly Household[]): string[] {
    return households.filter((household) => userManagesHousehold(user, household)).map((household) => household.id);
}

export function isNameOnlyRecipeContent(
    ingredients: readonly { name: string }[],
    instructions: string | readonly { type?: string; content?: string }[] | undefined,
): boolean {
    const hasIngredient = ingredients.some((ingredient) => ingredient.name.trim().length > 0);
    if (hasIngredient) return false;
    if (typeof instructions === 'string') return instructions.trim().length === 0;
    if (!instructions) return true;
    return !instructions.some((step) => {
        if (step.type === 'header') return false;
        return (step.content ?? '').trim().length > 0;
    });
}

/** A temporary recipe is finished once it has an ingredient and a real instruction step. */
export function recipeContentIsComplete(
    ingredients: readonly { name: string }[],
    instructions: string | readonly { type?: string; content?: string }[] | undefined,
): boolean {
    const hasIngredient = ingredients.some((ingredient) => ingredient.name.trim().length > 0);
    if (!hasIngredient) return false;
    if (typeof instructions === 'string') return instructions.trim().length > 0;
    return (instructions ?? []).some((step) => step.type !== 'header' && (step.content ?? '').trim().length > 0);
}

/** Existing name-only recipes count as temporary even before the flag was stored. */
export function recipeActsAsTemporary(
    recipe: Pick<Recipe, 'isTemporary' | 'ingredients' | 'instructions'> | undefined,
): boolean {
    if (!recipe) return true;
    if (recipe.isTemporary === true) return true;
    return isNameOnlyRecipeContent(recipe.ingredients ?? [], recipe.instructions);
}

export interface TemporaryRecipeVisibility {
    isTemporary?: boolean;
    isPublic?: boolean;
    sharedWithHouseholds?: string[];
}

export function constrainTemporaryRecipeVisibility<T extends TemporaryRecipeVisibility>(
    recipe: T,
    user: RecipeShareActor,
    households: readonly Household[],
): T {
    if (recipe.isTemporary !== true) return recipe;
    const allowed = new Set(managedHouseholdIds(user, households));
    return {
        ...recipe,
        isPublic: false,
        sharedWithHouseholds: (recipe.sharedWithHouseholds ?? []).filter((id) => allowed.has(id)),
    };
}

export function instructionContentForNameOnlyCheck(
    steps: readonly InstructionStep[],
    pendingDraft?: string,
): { type: InstructionStep['type']; content: string }[] {
    const mapped = steps.map((step) => ({ type: step.type, content: step.content }));
    const draft = pendingDraft?.trim();
    if (!draft) return mapped;
    return [...mapped, { type: 'step', content: draft }];
}
